/**
 * Paying an order out of the wallet.
 *
 * The arithmetic lives here rather than in the dialog because it decides which of two very
 * different screens a customer sees — enter a PIN, or top up first — and that decision is worth
 * testing without a browser. Nothing here is a wire shape.
 *
 * There is no PIN check here, and there must not be one. Whether a PIN is right is the vendor's
 * answer (`orders_pay` code 2, MYP-92); the app only checks it is six digits. The demo PIN that
 * used to live here — and was printed on screen — is gone.
 */

/** How much a wallet is short of covering an amount. Zero when it covers it. */
export function walletShortfallCents(balanceCents: number, amountCents: number): number {
  return Math.max(0, amountCents - balanceCents);
}

/**
 * Whether the wallet covers the amount outright.
 *
 * Exactly equal counts as covered: a balance of RM 128.50 pays a bill of RM 128.50 and leaves
 * nothing, which is a paid order rather than a shortfall of zero to be topped up.
 */
export function canPayFromWallet(balanceCents: number, amountCents: number): boolean {
  return walletShortfallCents(balanceCents, amountCents) === 0;
}

/**
 * The longest payment password the field accepts (Q10, 6 Oct 2026).
 *
 * The vendor's document calls it a 6-digit PIN, but the payment password it gave for the test
 * account is a word, and the server only compares an MD5 hash of whatever is sent — so it is any
 * text. The vendor has not said how long one can be (api-issues #39), so the cap is set well above
 * any real password: a lower one would cut a longer password short without saying so, and its
 * owner would get "wrong password" on every try. It only stops a runaway paste.
 */
export const PAY_PASSWORD_MAX_LENGTH = 64;

/**
 * Whether a payment password is the right shape to submit: not empty, and within the cap. Says
 * nothing about whether it is correct — only the vendor knows that.
 *
 * Never trimmed or filtered: a space or a capital letter is part of the password, and changing it
 * here would turn a correct password into "wrong password" on the server.
 */
export function isValidPayPassword(value: string): boolean {
  return value.length > 0 && value.length <= PAY_PASSWORD_MAX_LENGTH;
}
